import type { CompleteMicroSegmentationResult } from '../types/segmentationTypes'

export interface PropagationCandidate {
  id: string
  segmentation_result?: CompleteMicroSegmentationResult | null
}

export interface SelectPropagationTargetsInput<TCandidate extends PropagationCandidate> {
  candidates: TCandidate[]
  activeEditWitnessId: string
  groundTruthWitnessId?: string | null
}

export interface SelectPropagationTargetsResult<TCandidate extends PropagationCandidate> {
  protectedWitnessIds: Set<string>
  targetWitnesses: TCandidate[]
  isEditingGroundTruth: boolean
  isGroundTruthLocked: boolean
}

export const countUnits = (segmentationResult?: CompleteMicroSegmentationResult | null): number => {
  if (!segmentationResult?.chunk_results?.length) {
    return 0
  }
  return segmentationResult.chunk_results.reduce((sum, chunk) => sum + (chunk.micro_units?.length || 0), 0)
}

export const getSegmentationPropagatedFlag = (
  segmentationResult?: CompleteMicroSegmentationResult | null
): boolean | null => {
  const metadata = segmentationResult?.segmentation_metadata
  if (!metadata || typeof metadata !== 'object') {
    return null
  }

  const rawFlag = (metadata as Record<string, unknown>).propagated
  if (typeof rawFlag === 'boolean') {
    return rawFlag
  }
  if (typeof rawFlag !== 'string') {
    return null
  }

  const normalizedFlag = rawFlag.trim().toLowerCase()
  if (normalizedFlag === 'true') {
    return true
  }
  if (normalizedFlag === 'false') {
    return false
  }
  return null
}

export const isAutoPropagatedSegmentation = (
  segmentationResult?: CompleteMicroSegmentationResult | null
): boolean => getSegmentationPropagatedFlag(segmentationResult) === true

export const hasManualOrUnknownSegmentation = (
  segmentationResult?: CompleteMicroSegmentationResult | null
): boolean => countUnits(segmentationResult) > 0 && !isAutoPropagatedSegmentation(segmentationResult)

export const selectPropagationTargets = <TCandidate extends PropagationCandidate>(
  input: SelectPropagationTargetsInput<TCandidate>
): SelectPropagationTargetsResult<TCandidate> => {
  const groundTruthWitnessId = input.groundTruthWitnessId || ''
  const isEditingGroundTruth = Boolean(
    groundTruthWitnessId && input.activeEditWitnessId && groundTruthWitnessId === input.activeEditWitnessId
  )
  const isGroundTruthLocked = Boolean(
    groundTruthWitnessId && input.activeEditWitnessId && groundTruthWitnessId !== input.activeEditWitnessId
  )

  const protectedWitnessIds = new Set<string>()
  input.candidates.forEach(witness => {
    if (witness.id === input.activeEditWitnessId) {
      return
    }

    const isLockedGroundTruthWitness = isGroundTruthLocked && witness.id === groundTruthWitnessId
    const hasExistingSegmentation = countUnits(witness.segmentation_result) > 0
    const keepExistingSegmentation = isEditingGroundTruth
      ? hasManualOrUnknownSegmentation(witness.segmentation_result)
      : hasExistingSegmentation

    if (isLockedGroundTruthWitness || keepExistingSegmentation) {
      protectedWitnessIds.add(witness.id)
    }
  })

  return {
    protectedWitnessIds,
    targetWitnesses: input.candidates.filter(witness => !protectedWitnessIds.has(witness.id)),
    isEditingGroundTruth,
    isGroundTruthLocked
  }
}

